import React, { useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, Link } from "react-router-dom";
import { useSnackbar } from "../../contexts/SnackbarProvider";
import { useApi } from "../../contexts/ApiProvider";
import { useTranslation } from "react-i18next";

type TRequestEmail = {
  email: string;
};

const RequestPasswordResetForm = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const showMessage = useSnackbar();
  const api = useApi();
  const { t } = useTranslation();

  const handleCancel = () => {
    showMessage({
      text: t("Requesting password cancelled"),
      severity: "info",
    });
    navigate("/");
  };

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required(t("Required")),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      const response = await api.post<TRequestEmail, null>(
        "/tokens/reset",
        values
      );
      if (response.ok && response.body) {
        showMessage({
          text: t("Password reset requested successfully"),
          severity: "success",
        });
        navigate("/logout", {
          state: {
            title: t("Request sent ..."),
            message: t(
              "Check your email for the instructions to reset your password."
            ),
          },
        });
      } else {
        showMessage({
          text: t("Requesting password reset failed"),
          severity: "error",
        });
        navigate("/login");
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <TextField
        fullWidth
        autoFocus
        margin="normal"
        id="email"
        label={t("E-Mail")}
        type="text"
        disabled={isSubmitting}
        error={formik.touched.email && Boolean(formik.errors.email)}
        helperText={formik.touched.email && formik.errors.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.email}
      ></TextField>
      <Box mt={2} mb={2} display="flex" flexDirection="row">
        <Button type="submit" disabled={isSubmitting}>
          {t("Request")}
        </Button>
        <Link to="/">
          <Button type="reset" disabled={isSubmitting} onClick={handleCancel}>
            {t("Cancel")}
          </Button>
        </Link>
      </Box>
    </form>
  );
};

export default RequestPasswordResetForm;
